import React, {useEffect} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {QWActionButtons} from "../quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../quick_view/QWLocalizationInfo";
import {QWPictograms} from "../quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../quick_view/QWPropertyInfoHeading";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferExpandablePanelQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const offerTypeHeaderTitle = getOfferTypeNamePluralWhat(offer.type);

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    if (isLoading) {
        return <Loader fullWidth size="lg" />;
    }
    return (
        <div className={wrapper}>
            <div className={headerStyle}>
                <QWHeaderWithPagination
                    handleQuickViewClose={handleQuickViewClose}
                    setSelectedIndex={setSelectedIndex}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    onNextClickEvent={nextClickAnalyticsEvent}
                    onPrevClickEvent={prevClickAnalyticsEvent}
                    headerTitle={offerTypeHeaderTitle}
                />
            </div>
            <div className={contentStyle}>
                <QWImageWithButton
                    propertyLink={propertyLink}
                    alt={offer.name}
                    firstImageSrc={propertyPlanImage}
                    secondImageSrc={offer.main_image.m_img_760x428}
                    clickEvent={handleGalleryButtonClick}
                    onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                    className={imageStyle}
                    buttonClassName={imageButtonStyle}
                    textInButtonsDisabled
                />

                <QWPropertyInfoHeading
                    rooms={selectedProperty.rooms}
                    offerType={offer.type}
                    offerName={offer.name}
                    highlightBoldText
                    textVariant="headline_4"
                    className={infoHeadingStyle}
                />

                <PoiItemsList offer={offer} className={css({pb: 3, pt: 0.5})} />
                <QWLocalizationInfo
                    address={offer.address}
                    distanceFromRegion={offer.stats?.distance_from_region}
                    offerType={offer.type}
                    className={css({mb: 5, flexDirection: "column-reverse"})}
                    textVariant="info_txt_1"
                />

                <QWPictograms selectedProperty={selectedProperty} className={css({mb: 9})} />

                <QWActionButtons
                    propertyLink={propertyLink}
                    vendorId={offer.vendor.id}
                    offerId={offer.id}
                    propertyId={selectedProperty.id}
                    propertyPrice={selectedProperty.price ?? null}
                    applicationSource={ApplicationSource.QuickViewPropertyModal}
                    applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                    clickEvent={handleDetailsButtonClick}
                    hideApplicationButton={isForSaleOverLimit}
                    className={buttonsStyle}
                    buttonSize="x-small"
                    buttonClassName={buttonStyle}
                />
            </div>
        </div>
    );
};

const wrapper = css({
    display: "flex",
    flexDirection: "column",
    zIndex: 1,
    backgroundColor: "var(--colors-gray-100)",
    p: 0
});

const buttonsStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    position: "fixed",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-4)",
    py: "1.6rem !important",
    px: 1,
    gap: "1.6rem !important",
    width: "100%"
});

const buttonStyle = css({
    width: "100%"
});

const headerStyle = css({
    backgroundColor: "var(--colors-white)",
    px: 1.5
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    overflowX: "hidden",
    overflowY: "auto",
    maxHeight: "calc(100vh - 60px - 54px)",
    pt: 0,
    px: 1.5,
    pb: 7,
    position: "relative"
});

const imageStyle = css({
    width: "100vw",
    minWidth: "100vw",
    ml: -1.5
});

const imageButtonStyle = css({
    minWidth: "calc(var(--globals-baseUnit) * 4)",
    maxWidth: "calc(var(--globals-baseUnit) * 4)",
    maxHeight: "calc(var(--globals-baseUnit) * 4)",
    minHeight: "calc(var(--globals-baseUnit) * 4)",
    p: 0
});

const infoHeadingStyle = css({
    px: 0
});
import React, {useEffect} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {QWActionButtons} from "../quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../quick_view/QWLocalizationInfo";
import {QWPictograms} from "../quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../quick_view/QWPropertyInfoHeading";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferExpandablePanelQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const offerTypeHeaderTitle = getOfferTypeNamePluralWhat(offer.type);

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    if (isLoading) {
        return <Loader fullWidth size="lg" />;
    }
    return (
        <div className={wrapper}>
            <div className={headerStyle}>
                <QWHeaderWithPagination
                    handleQuickViewClose={handleQuickViewClose}
                    setSelectedIndex={setSelectedIndex}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    onNextClickEvent={nextClickAnalyticsEvent}
                    onPrevClickEvent={prevClickAnalyticsEvent}
                    headerTitle={offerTypeHeaderTitle}
                />
            </div>
            <div className={contentStyle}>
                <QWImageWithButton
                    propertyLink={propertyLink}
                    alt={offer.name}
                    firstImageSrc={propertyPlanImage}
                    secondImageSrc={offer.main_image.m_img_760x428}
                    clickEvent={handleGalleryButtonClick}
                    onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                    className={imageStyle}
                    buttonClassName={imageButtonStyle}
                    textInButtonsDisabled
                />

                <QWPropertyInfoHeading
                    rooms={selectedProperty.rooms}
                    offerType={offer.type}
                    offerName={offer.name}
                    highlightBoldText
                    textVariant="headline_4"
                    className={infoHeadingStyle}
                />

                <PoiItemsList offer={offer} className={css({pb: 3, pt: 0.5})} />
                <QWLocalizationInfo
                    address={offer.address}
                    distanceFromRegion={offer.stats?.distance_from_region}
                    offerType={offer.type}
                    className={css({mb: 5, flexDirection: "column-reverse"})}
                    textVariant="info_txt_1"
                />

                <QWPictograms selectedProperty={selectedProperty} className={css({mb: 9})} />

                <QWActionButtons
                    propertyLink={propertyLink}
                    vendorId={offer.vendor.id}
                    offerId={offer.id}
                    propertyId={selectedProperty.id}
                    propertyPrice={selectedProperty.price ?? null}
                    applicationSource={ApplicationSource.QuickViewPropertyModal}
                    applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                    clickEvent={handleDetailsButtonClick}
                    hideApplicationButton={isForSaleOverLimit}
                    className={buttonsStyle}
                    buttonSize="x-small"
                    buttonClassName={buttonStyle}
                />
            </div>
        </div>
    );
};

const wrapper = css({
    display: "flex",
    flexDirection: "column",
    zIndex: 1,
    backgroundColor: "var(--colors-gray-100)",
    p: 0
});

const buttonsStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    position: "fixed",
    bottom: 0,
    left: 0,
    backgroundColor: "var(--colors-white)",
    boxShadow: "var(--shadow-4)",
    py: "1.6rem !important",
    px: 1,
    gap: "1.6rem !important",
    width: "100%"
});

const buttonStyle = css({
    width: "100%"
});

const headerStyle = css({
    backgroundColor: "var(--colors-white)",
    px: 1.5
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    overflowX: "hidden",
    overflowY: "auto",
    maxHeight: "calc(100vh - 60px - 54px)",
    pt: 0,
    px: 1.5,
    pb: 7,
    position: "relative"
});

const imageStyle = css({
    width: "100vw",
    minWidth: "100vw",
    ml: -1.5
});

const imageButtonStyle = css({
    minWidth: "calc(var(--globals-baseUnit) * 4)",
    maxWidth: "calc(var(--globals-baseUnit) * 4)",
    maxHeight: "calc(var(--globals-baseUnit) * 4)",
    minHeight: "calc(var(--globals-baseUnit) * 4)",
    p: 0
});

const infoHeadingStyle = css({
    px: 0
});
